import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Makes dashboard rows go somewhere.
 *
 * Before this, exactly one of the fifteen dashboard cards navigated anywhere.
 * Tray Line Warnings listed 126 residents by name and not one row was
 * clickable — you read a name off the dashboard, then went and found that
 * person yourself through the sidebar. The dashboard reported problems and
 * left you to locate every one of them by hand.
 *
 * Each card knows which section of the resident record answers its own
 * question, so a weight alert opens Weight History and an allergy change
 * opens Food Allergies. `?tab=` deep-linking is already supported by
 * `useSectionNav`, so this is only ever building a URL.
 */

/** Section keys on the resident record. Must match `NAV_ITEMS` in ResidentDetail. */
export const RESIDENT_TAB = {
  overview: 'overview',
  primaryInfo: 'primary-info',
  dietOrder: 'diet-info',
  allergies: 'allergies',
  trayLine: 'tray-line',
  mealPattern: 'meal-pattern',
  selectiveMenu: 'selective-menu',
  weightHistory: 'weight-history',
  intake: 'intake',
  assessments: 'assessments',
  interactionLog: 'activity-log',
} as const;

export type ResidentTab = (typeof RESIDENT_TAB)[keyof typeof RESIDENT_TAB];

export function useDashboardNav() {
  const navigate = useNavigate();

  /** Open a resident, optionally straight to the section that explains the row. */
  const openResident = useCallback(
    (patientId: number | null | undefined, tab?: ResidentTab) => {
      if (patientId == null || patientId <= 0) return;
      navigate(tab ? `/people/${patientId}?tab=${tab}` : `/people/${patientId}`);
    },
    [navigate],
  );

  /** Guests live on their own profile route rather than the resident record. */
  const openGuest = useCallback(
    (patientId: number | null | undefined) => {
      if (patientId == null || patientId <= 0) return;
      navigate(`/people/guests/profile/${patientId}`);
    },
    [navigate],
  );

  /**
   * Props for a card's grid (CardGrid / AppGrid) that make its rows behave like links.
   *
   * `getId` rather than a fixed field name because the dashboard DTOs are
   * inconsistent about it — some carry `pKey_Patient`, others `fKey_Patient`.
   *
   * A click opens the row; so does Enter on the keyboard row (AppGrid reports
   * Enter as `onRowDblClick`), so the cards work without a mouse. The class
   * gives the rows a pointer and the blue hover tint: the rows are the only
   * clickable thing in the card, and there is no room for a chevron.
   */
  const linkProps = useCallback(
    <T,>(open: (row: T) => void) => ({
      onRowClick: (e: { data: T }) => open(e.data),
      onRowDblClick: (e: { data: T }) => open(e.data),
      className: 'card-grid-link',
    }),
    [],
  );

  const rowLinkProps = useCallback(
    <T,>(getId: (row: T) => number | null | undefined, tab?: ResidentTab) =>
      linkProps<T>((row) => openResident(getId(row), tab)),
    [linkProps, openResident],
  );

  /** As rowLinkProps, for the Guests card: rows open the guest profile. */
  const guestLinkProps = useCallback(
    <T,>(getId: (row: T) => number | null | undefined) =>
      linkProps<T>((row) => openGuest(getId(row))),
    [linkProps, openGuest],
  );

  return useMemo(
    () => ({ openResident, openGuest, rowLinkProps, guestLinkProps }),
    [openResident, openGuest, rowLinkProps, guestLinkProps],
  );
}
